<template>
	<Col class="menu-container">
		<WorkflowVersionHistoryMenuCurrentPublished
			:workflow="workflow"
			@restore-version="restoreVersion"
			:isLoading="isLoadingRestoreVersion"
			:workflowCurrentVersion="workflowCurrentVersion"
		/>
		<div class="divider-container width-100 my-4" />
		<WorkflowVersionHistoryMenuDraftVersion
			:workflow="workflow"
			:isLoading="isLoadingSaveDraft"
			@save-version="saveDraftVersion"
			:workflowCurrentDraft="workflowCurrentDraft"
			:workflowCurrentVersion="workflowCurrentVersion"
		/>
		<MaybeUpgradeRequiredIndicator :billingFeatureId="billingFeatureId" class="mt-5 open-version-history-button">
			<template #default="{ indicator, isUpgradeRequired }">
				<AnemicButton @click="() => openVersionHistory(isUpgradeRequired, indicator)" tile class="inner">
					<Row>
						{{ isVersionHistoryOpen ? 'Close' : 'Open' }} version history
						<component :is="indicator" v-if="isUpgradeRequired" class="ml-4" :billingFeatureId="billingFeatureId" iconOnly />
					</Row>
				</AnemicButton>
			</template>
		</MaybeUpgradeRequiredIndicator>
	</Col>
</template>

<script>
import MaybeUpgradeRequiredIndicator from '@/components/Billing/MaybeUpgradeRequiredIndicator.vue';
import { FeatureTypes } from '@/components/Billing/billing-types';
import WorkflowVersionHistoryMenuCurrentPublished from '@/components/WorkflowVersionHistory/WorkflowVersionHistoryMenuCurrentPublished.vue';
import WorkflowVersionHistoryMenuDraftVersion from '@/components/WorkflowVersionHistory/WorkflowVersionHistoryMenuDraftVersion.vue';
import WorkflowVersionService from '@/components/WorkflowVersionHistory/workflow-version-service';
import { BUILDER_VERSION_HISTORY_ROUTE_NAME } from '@/router/router.const.js';
import { restoreSelectedWorkflowSpecificVersionAsDraft } from '@/store/modules/selectedWorkflow/actions';
import { useSelectedWorkflowVersionId } from '@/store/modules/workflows/modules/versions/getters.js';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
export default {
	name: 'WorkflowVersionHistoryMenuContent',
	components: {
		WorkflowVersionHistoryMenuDraftVersion,
		WorkflowVersionHistoryMenuCurrentPublished,
		MaybeUpgradeRequiredIndicator,
	},
	props: {
		workflowCurrentVersion: { type: Object },
		workflow: { type: Object, required: true },
		workflowCurrentDraft: { type: Object, required: true },
	},
	computed: {
		...mapGettersShorthand({ useSelectedWorkflowVersionId }),
		isVersionHistoryOpen() {
			return this.useSelectedWorkflowVersionId;
		},
		doesWorkflowNeedPublish() {
			return this.workflow.draft_pending_changes_flag || !this.hasWorkflowEverBeenPublished;
		},
		hasWorkflowEverBeenPublished() {
			return this.workflow.is_published;
		},
		billingFeatureId() {
			return FeatureTypes.VersionHistory;
		},
	},
	methods: {
		...mapActionsShorthand({ restoreSelectedWorkflowSpecificVersionAsDraft }),
		async restoreVersion() {
			try {
				this.isLoadingRestoreVersion = true;
				await WorkflowVersionService.restoreVersion({
					router: this.$router,
					workflowId: this.workflow.id,
					navigateToVersionAfterCreation: true, // todo: verify with product this
					version: this.workflowCurrentVersion.id,
					hasConfiguration: this.doesVersionHaveConfiguration(this.workflowCurrentVersion),
					restoreSelectedWorkflowSpecificVersionAsDraft: this.restoreSelectedWorkflowSpecificVersionAsDraft,
				});
				this.$toast.success('Version restored successfully.');
			} catch (error) {
				console.error(error);
				this.$toast.error('Failed to restore version, please try again later.');
			} finally {
				this.isLoadingRestoreVersion = false;
			}
		},
		async saveDraftVersion() {
			try {
				this.isLoadingSaveDraft = true;
				await WorkflowVersionService.restoreVersion({
					router: this.$router,
					workflowId: this.workflow.id,
					navigateToVersionAfterCreation: false,
					version: this.workflowCurrentDraft.id,
					hasConfiguration: this.doesVersionHaveConfiguration(this.workflowCurrentDraft),
					restoreSelectedWorkflowSpecificVersionAsDraft: this.restoreSelectedWorkflowSpecificVersionAsDraft,
				});
				this.$toast.success('Draft version saved');
			} catch (error) {
				console.error(error);
				this.$toast.error('Failed to save draft version, please try again later.');
			} finally {
				this.isLoadingSaveDraft = false;
			}
		},
		doesVersionHaveConfiguration(version) {
			return version.user_specific_config_options || version.general_config_options;
		},
		closeMenu() {
			this.$emit('close');
		},
		openVersionHistory(isUpgradeRequired, indicator) {
			if (isUpgradeRequired && indicator?.setup) {
				indicator.setup().openUpgrade(null, this.billingFeatureId);
			} else {
				if (this.isVersionHistoryOpen) {
					WorkflowVersionService.closeLeftSideDrawerSpecialRouterView(this.$router);
				} else {
					const versionId = this.workflowCurrentDraft.id;
					this.$router.push({ name: BUILDER_VERSION_HISTORY_ROUTE_NAME, params: { versionId } });
				}
				this.closeMenu();
			}
		},
	},
	data() {
		return {
			isLoadingSaveDraft: false,
			isLoadingRestoreVersion: false,
		};
	},
};
</script>

<style scoped lang="scss">
.menu-container {
	padding: 16px 10px;
	border-radius: 8px;
	background: var(--white);
	border: 1px solid #dfe4ef;
	box-shadow: 0px 3px 16px #00000039;
}
.divider-container {
	height: 0px;
	opacity: 0.26;
	margin: 0px 2px;
	border: 1px solid #d6ddea;
}
.open-version-history-button {
	height: 35px !important;
	border-radius: 10px !important;
	background: #ffffff !important;
	border: 1px solid #5858ec !important;
	.inner {
		width: 100% !important;
		height: 33px !important;
		letter-spacing: 0px !important;
		text-transform: none !important;
		font-size: 14px !important;
		color: #5858ec !important;
		font-weight: normal !important;
		background: #ffffff !important;
		border-radius: 10px !important;
	}
}
</style>
